import type { ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'
import { PillButton } from '@maria-cavalheiro-website/shared-ui-web'

import { EbookReader } from './ebook-reader.tsx'
import { startIndexFor } from './ebook-pages.ts'
import { useEbook } from './use-ebook.ts'

export interface EbookLauncherProps {
  readonly poems: readonly Poem[]
  /**
   * Where the book opens. Omitted on the index, where it opens at page one; the
   * poem route passes its own slug so the book opens on the poem being read.
   */
  readonly startSlug?: string
  readonly label: string
}

/**
 * The button that opens the book, and the owner of its state.
 *
 * THE READER IS MOUNTED ON DEMAND, WHICH IS THE WHOLE REASON THIS SPLIT EXISTS.
 * Rendering the dialog unconditionally would put all twenty-three poems into the
 * prerendered HTML of every route carrying a launcher — duplicate content for a
 * crawler on the index, a second copy of the poem on its own route, and a document
 * several times its weight for a reader who never presses the button. Mounting it
 * on the click means the markup exists only after a gesture, which is also the
 * gesture `requestFullscreen` requires.
 *
 * NOTHING RENDERS DIFFERENTLY BEFORE AND AFTER HYDRATION. `useEbook` starts
 * closed on the server and on the client's first pass alike, so the button is all
 * that is ever prerendered and the trees are byte-identical.
 */
export function EbookLauncher({ poems, startSlug, label }: EbookLauncherProps): ReactElement {
  const ebook = useEbook(poems.length, startIndexFor(startSlug))

  // `noUncheckedIndexedAccess` is on, and this is modelled rather than asserted
  // away: `stepPage` keeps the index inside the collection, so the only way this
  // is absent is an empty book. A launcher over no poems opens nothing, which is
  // the honest outcome — and it is reachable from a test, unlike the non-null
  // assertion that would otherwise stand here and that the lint config bans.
  const current = poems[ebook.index]

  return (
    <>
      <PillButton variant="secondary" onClick={ebook.open} icon="book-open">
        {label}
      </PillButton>

      {ebook.isOpen && current !== undefined ? (
        <EbookReader
          poem={current}
          index={ebook.index}
          total={poems.length}
          isPlaying={ebook.isPlaying}
          onClose={ebook.close}
          onPrevious={ebook.previous}
          onNext={ebook.next}
          onTogglePlay={ebook.togglePlay}
        />
      ) : null}
    </>
  )
}
